import { Trans, useLingui } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { DateInput } from '@startuptools/ui/DateInput';
import { Box } from '@startuptools/ui/Box';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { useWpaSetDurationMutation } from '../../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Button } from '@startuptools/ui/Button';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { GqlSnrBond, GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';

type FormValues = {
  warrantStartDate: DateTime;
  warrantEndDate: DateTime;
};

export const SnrBasedStepDuration = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'warrantStartDate' | 'warrantEndDate'> & {
    snrBond?: Pick<GqlSnrBond, 'id' | 'decisionDate' | 'shareSubEndDate'> | null;
  };
}) => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const [doSetDuration, { loading }] = useWpaSetDurationMutation({ refetchQueries: [WarrantProgramDocument] });

  const snrDecisionDate = DateTime.fromISO(warrantProgram.snrBond!.decisionDate!);
  const snrSubEndDate = DateTime.fromISO(warrantProgram.snrBond!.shareSubEndDate!);

  const defaultStartDate = warrantProgram.warrantStartDate
    ? DateTime.fromISO(warrantProgram.warrantStartDate)
    : snrDecisionDate;
  const defaultEndDate = warrantProgram.warrantEndDate
    ? DateTime.fromISO(warrantProgram.warrantEndDate)
    : snrSubEndDate;

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { warrantStartDate: defaultStartDate, warrantEndDate: defaultEndDate },
  });

  const warrantStartDate = watch('warrantStartDate');

  const onSubmit: SubmitHandler<FormValues> = async ({ warrantStartDate, warrantEndDate }) => {
    await doSetDuration({
      variables: {
        companyId,
        input: {
          id: warrantProgram.id,
          warrantStartDate: warrantStartDate.toUTC(0, { keepLocalTime: true }).toISO()!,
          warrantEndDate: warrantEndDate.toUTC(0, { keepLocalTime: true }).toISO()!,
        },
      },
    });
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="How long is the option program?" />
        </Typography>

        <Box maxWidth={300}>
          <Controller
            control={control}
            name="warrantStartDate"
            rules={{
              validate: value => {
                if (value > snrSubEndDate) {
                  return i18n.t('The date is after the share subscription end date registered with Bolagsverket');
                }
                if (value < snrDecisionDate) {
                  return i18n.t('The date is before the decision date registered with Bolagsverket');
                }
                return true;
              },
            }}
            render={({ field, formState }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Set vesting start date" />
                </FormLabel>
                {formState.errors.warrantStartDate && (
                  <FormHelperText>{formState.errors.warrantStartDate.message}</FormHelperText>
                )}
                <DateInput {...field} minDate={snrDecisionDate} maxDate={snrSubEndDate} />
              </FormControl>
            )}
          />
        </Box>

        <Box maxWidth={300}>
          <Controller
            control={control}
            name="warrantEndDate"
            rules={{
              validate: value => {
                if (value < warrantStartDate) {
                  return i18n.t('End date must be after start date');
                }
                if (value > snrSubEndDate) {
                  return i18n.t('The date is before the share subscription end date registered with Bolagsverket');
                }
                return true;
              },
            }}
            render={({ field, formState }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Set vesting end date" />
                </FormLabel>
                {formState.errors.warrantEndDate && (
                  <FormHelperText>{formState.errors.warrantEndDate.message}</FormHelperText>
                )}
                <DateInput {...field} minDate={warrantStartDate} maxDate={snrSubEndDate} />
              </FormControl>
            )}
          />
        </Box>

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
